/* eslint-disable */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSetRecoilState } from 'recoil'
import { ArrowLeft, Bug, PlayCircle } from 'lucide-react'
import { Button } from '../../Components/ui/button'
import PaymentStatusModal from '../../Components/modals/OneTimePayment'
import KycPopup from '../../Components/Popups/KycPopup'
import { useModal } from '../../hooks/use-modal-store'
import { messageModalState } from '../../store/Feedback'
import { useErrorPopupStore } from '../../store/useErrorPopupStore'
import ApiErrorState from '../../Components/Errors/ApiErrorState'

const noop = () => {}

const DebugPlanFeatures = () => (
  <ul className='space-y-2 text-sm text-black-400'>
    <li>Dedicated plan review</li>
    <li>Portfolio allocation guidance</li>
    <li>Mandate based subscription flow</li>
  </ul>
)

const mockConstituents = [
  {
    id: 'debug-fund-1',
    amcCode: 'PPFAS',
    legalName: 'Debug Flexi Cap Fund Direct Growth',
    schemeName: 'Debug Flexi Cap Fund Direct Growth',
    sname: 'Debug Flexi Cap Fund',
    assetClass: 'Equity',
    weightage: 55,
    investmentAmount: 5500,
    installmentAmt: 5500,
    bseSchemeCode: 'DBG001',
  },
  {
    id: 'debug-fund-2',
    amcCode: 'HDFC',
    legalName: 'Debug Balanced Advantage Fund Direct Growth',
    schemeName: 'Debug Balanced Advantage Fund Direct Growth',
    sname: 'Debug Balanced Advantage Fund',
    assetClass: 'Hybrid',
    weightage: 30,
    investmentAmount: 3000,
    installmentAmt: 3000,
    bseSchemeCode: 'DBG002',
  },
  {
    id: 'debug-fund-3',
    amcCode: 'ICICI',
    legalName: 'Debug Liquid Fund Direct Growth',
    schemeName: 'Debug Liquid Fund Direct Growth',
    sname: 'Debug Liquid Fund',
    assetClass: 'Debt',
    weightage: 15,
    investmentAmount: 1500,
    installmentAmt: 1500,
    bseSchemeCode: 'DBG003',
  },
]

const buildBasket = (statuses) => ({
  debugBasket: mockConstituents.map((fund, index) => ({
    ...fund,
    customerBasketName: 'Debug Basket',
    status: statuses[index] || statuses[0],
  })),
})

const mockRedeemConstituents = mockConstituents.map((fund, index) => ({
  ...fund,
  schemeName: fund.schemeName,
  redemptionType: index === 0 ? 'VALUE' : 'UNITS',
  withdrawableAmount: index === 0 ? 2400 : undefined,
  withdrawableUnits: index === 0 ? undefined : 12 + index,
  nav: 180 + index * 20,
  currentValue: 2400 + index * 500,
}))

const mockMandateDetails = {
  fetchUserPlan: {
    createdTimestamp: '2026-07-22T10:00:00.000Z',
    amountChosen: 1999,
  },
  userBankDetails: {
    accountHolderName: 'Debug User',
    accountNo: 'XXXX1234',
  },
  handleRevoke: noop,
  handleUpdate: noop,
}

const mockSubscription = {
  userPlan: {
    title: 'Debug Plan',
    amount: 999,
    features: DebugPlanFeatures,
  },
  mandateStatus: 'PENDING',
  createMandate: noop,
}

const mockPaymentData = {
  amount: '10,000',
  paymentMode: 'UPI',
}

const DebugPopups = () => {
  const navigate = useNavigate()
  const { onOpen } = useModal()
  const setMessageModalState = useSetRecoilState(messageModalState)
  const showErrorPopup = useErrorPopupStore((state) => state.showErrorPopup)
  const [paymentStatus, setPaymentStatus] = useState('inprogress')
  const [showPaymentStatus, setShowPaymentStatus] = useState(false)
  const [showKycPrompt, setShowKycPrompt] = useState(false)

  const openPaymentStatus = (status) => {
    setPaymentStatus(status)
    setShowPaymentStatus(true)
  }

  const popupGroups = [
    {
      title: 'Global Popups',
      items: [
        {
          name: 'Message popup - success',
          trigger: 'openPopup',
          onClick: () =>
            onOpen('openPopup', {
              title: 'Debug success',
              message: 'This is the global success popup.',
              severity: 'success',
              redirectBackTo: '/dashboard/debug-popups',
              forwardTo: '/dashboard/debug-popups',
            }),
        },
        {
          name: 'Message popup - info',
          trigger: 'openPopup',
          onClick: () =>
            onOpen('openPopup', {
              title: 'Debug info',
              message: 'This is the global info popup.',
              severity: 'info',
              redirectBackTo: '/dashboard/debug-popups',
              forwardTo: '/dashboard/debug-popups',
            }),
        },
        {
          name: 'App update popup',
          trigger: 'openPopup / app_update',
          onClick: () =>
            onOpen('openPopup', {
              title: 'Our app just got better',
              message:
                'We refreshed the experience with sharper visuals, smoother motion, and improved readability across the app.',
              severity: 'app_update',
              hideCloseButton: true,
              disableDismiss: true,
              primaryActionText: 'Refresh app',
              onPrimaryAction: () => window.location.reload(),
            }),
        },
        {
          name: 'Message popup - default',
          trigger: 'openPopup',
          onClick: () =>
            onOpen('openPopup', {
              title: 'Debug notice',
              message: 'This is the global fallback popup style.',
              severity: 'notice',
              redirectBackTo: '/dashboard/debug-popups',
              forwardTo: '/dashboard/debug-popups',
            }),
        },
        {
          name: 'Error popup',
          trigger: 'useErrorPopupStore',
          onClick: () =>
            showErrorPopup(
              'Debug error',
              'This is the shared error popup controlled by useErrorPopupStore.',
              noop,
            ),
        },
      ],
    },
    {
      title: 'Legacy Message Modal',
      items: [
        {
          name: 'Legacy info modal',
          trigger: 'messageModalState',
          onClick: () =>
            setMessageModalState({
              message: 'This is the legacy Recoil info modal.',
              additionaInfo: 'Optional supporting text.',
              severity: 'info',
              callback: noop,
            }),
        },
        {
          name: 'Legacy error modal',
          trigger: 'messageModalState',
          onClick: () =>
            setMessageModalState({
              title: 'Debug legacy error',
              message: 'This is the legacy Recoil error modal.',
              severity: 'error',
            }),
        },
        {
          name: 'Legacy KYC modal',
          trigger: 'messageModalState + linkMessage',
          onClick: () =>
            setMessageModalState({
              message: 'Complete KYC to continue.',
              severity: 'info',
              linkMessage: true,
            }),
        },
      ],
    },
    {
      title: 'SIP, Mandate, And Admin Popups',
      items: [
        {
          name: 'SIP popup - success',
          trigger: 'openSIPPopup',
          onClick: () =>
            onOpen('openSIPPopup', {
              title: 'SIP registered',
              message: 'Your debug SIP registration completed successfully.',
              severity: 'success',
            }),
        },
        {
          name: 'SIP popup - info',
          trigger: 'openSIPPopup',
          onClick: () =>
            onOpen('openSIPPopup', {
              title: 'SIP pending',
              message: 'Your debug SIP is pending confirmation.',
              severity: 'info',
            }),
        },
        {
          name: 'Rebalancing popup',
          trigger: 'openSIPPopup / rebalancing',
          onClick: () =>
            onOpen('openSIPPopup', {
              title: 'Rebalancing required',
              message:
                'Your debug portfolio needs rebalancing to bring the allocation back in line with the target plan.',
              severity: 'rebalancing',
            }),
        },
        {
          name: 'Mandate popup',
          trigger: 'openMandatePopup',
          onClick: () =>
            onOpen('openMandatePopup', {
              title: 'Mandate created',
              message: 'Your debug mandate has been created.',
              severity: 'success',
              closeButton: true,
              redirectBackTo: '/dashboard/debug-popups',
              showExploreButton: true,
              exploreButtonText: 'Continue',
              forwardTo: '/dashboard/debug-popups',
            }),
        },
        {
          name: 'Mandate registration loading page',
          trigger: '/dashboard/mutual-funds/mandate',
          detail: 'MFMandatePage loading and redirect state.',
          actionLabel: 'Open page',
          onClick: () => navigate('/dashboard/mutual-funds/mandate'),
        },
        {
          name: 'Admin popup',
          trigger: 'openAdminPopup',
          onClick: () =>
            onOpen('openAdminPopup', {
              title: 'Admin action complete',
              message: 'This tests the admin popup shell.',
              severity: 'success',
            }),
        },
      ],
    },
    {
      title: 'Basket Order Popups',
      items: [
        {
          name: 'Basket SIP success',
          trigger: 'openBasketPopup / sipsuccess',
          onClick: () =>
            onOpen('openBasketPopup', {
              title: 'SIP order placed',
              message: 'Your debug basket SIP order has been placed.',
              severity: 'sipsuccess',
            }),
        },
        {
          name: 'Basket order success',
          trigger: 'openBasketPopup / success',
          onClick: () =>
            onOpen('openBasketPopup', {
              severity: 'success',
              baskets: buildBasket(['SUCCESS']),
            }),
        },
        {
          name: 'Basket order failed',
          trigger: 'openBasketPopup / error',
          onClick: () =>
            onOpen('openBasketPopup', {
              severity: 'error',
              baskets: buildBasket(['FAILED']),
            }),
        },
        {
          name: 'Basket partial success',
          trigger: 'openBasketPopup / partial',
          onClick: () =>
            onOpen('openBasketPopup', {
              severity: 'partial',
              baskets: buildBasket(['SUCCESS', 'FAILED', 'SUCCESS']),
            }),
        },
      ],
    },
    {
      title: 'Action Plan Popups',
      items: [
        {
          name: 'Action plan SIP success',
          trigger: 'openActionPlanPopup / sipsuccess',
          onClick: () =>
            onOpen('openActionPlanPopup', {
              title: 'Action plan SIP registered',
              message: 'Your debug action plan SIP has been registered.',
              severity: 'sipsuccess',
              redirectTo: '/dashboard/debug-popups',
              goBack: '/dashboard/debug-popups',
            }),
        },
        {
          name: 'Action plan success',
          trigger: 'openActionPlanPopup / success',
          onClick: () =>
            onOpen('openActionPlanPopup', {
              title: 'Action plan order successful',
              message: 'Your debug action plan order completed.',
              severity: 'success',
              redirectTo: '/dashboard/debug-popups',
              goBack: '/dashboard/debug-popups',
            }),
        },
        {
          name: 'Action plan info',
          trigger: 'openActionPlanPopup / info',
          onClick: () =>
            onOpen('openActionPlanPopup', {
              title: 'Action plan info',
              message: 'This is the action plan info popup.',
              severity: 'info',
              redirectTo: '/dashboard/debug-popups',
              goBack: '/dashboard/debug-popups',
            }),
        },
        {
          name: 'Action plan failed',
          trigger: 'openActionPlanPopup / error',
          onClick: () =>
            onOpen('openActionPlanPopup', {
              severity: 'error',
              goBack: '/dashboard/debug-popups',
              baskets: buildBasket(['FAILED']),
            }),
        },
        {
          name: 'Action plan partial',
          trigger: 'openActionPlanPopup / partial',
          onClick: () =>
            onOpen('openActionPlanPopup', {
              severity: 'partial',
              goBack: '/dashboard/debug-popups',
              baskets: buildBasket(['SUCCESS', 'FAILED', 'SUCCESS']),
            }),
        },
      ],
    },
    {
      title: 'KYC Popups',
      items: [
        {
          name: 'KYC auto approved',
          trigger: 'openKycPopup / auto_approved',
          detail: 'Production button logs out.',
          onClick: () =>
            onOpen('openKycPopup', {
              title: 'KYC completed',
              message: 'Your debug KYC has been approved.',
              severity: 'auto_approved',
            }),
        },
        {
          name: 'KYC nominee confirmation',
          trigger: 'openKycPopup / nominee_confirmation',
          detail: 'Production button logs out.',
          onClick: () =>
            onOpen('openKycPopup', {
              title: 'Confirm nominee',
              message: 'Approve the debug nominee confirmation email from BSE.',
              severity: 'nominee_confirmation',
            }),
        },
        {
          name: 'KYC CVL KRA pending',
          trigger: 'openKycPopup / cvl_kra_pending',
          onClick: () =>
            onOpen('openKycPopup', {
              title: 'KYC update needed',
              message: 'Your debug CVL KRA status needs offline assistance.',
              severity: 'cvl_kra_pending',
            }),
        },
        {
          name: 'KYC auto declined',
          trigger: 'openKycPopup / auto_declined',
          onClick: () =>
            onOpen('openKycPopup', {
              title: 'KYC declined',
              message: 'Your debug KYC did not go through.',
              severity: 'auto_declined',
              handleRedirect: noop,
            }),
        },
        {
          name: 'KYC user cancelled',
          trigger: 'openKycPopup / user_cancelled',
          onClick: () =>
            onOpen('openKycPopup', {
              title: 'KYC cancelled',
              message: 'This tests the user cancelled KYC state.',
              severity: 'user_cancelled',
              handleRedirect: noop,
            }),
        },
        {
          name: 'KYC needs review',
          trigger: 'openKycPopup / needs_review',
          onClick: () =>
            onOpen('openKycPopup', {
              title: 'KYC under review',
              message: 'Your debug KYC is under review.',
              severity: 'needs_review',
              handleRedirect: noop,
            }),
        },
        {
          name: 'KYC failure',
          trigger: 'openKycPopup / failure',
          onClick: () =>
            onOpen('openKycPopup', {
              title: 'KYC failed',
              message: 'This tests the KYC failure popup.',
              severity: 'failure',
              handleRedirect: noop,
            }),
        },
        {
          name: 'KYC document prompt',
          trigger: 'KycPopup component',
          onClick: () => setShowKycPrompt(true),
        },
      ],
    },
    {
      title: 'Breakdowns And Forms',
      items: [
        {
          name: 'MF investment breakdown',
          trigger: 'openMFBreakdown',
          onClick: () =>
            onOpen('openMFBreakdown', {
              amount: 10000,
              constituents: mockConstituents,
            }),
        },
        {
          name: 'MF redeem breakdown',
          trigger: 'openMFRedeemBreakdown',
          onClick: () =>
            onOpen('openMFRedeemBreakdown', {
              amount: 7000,
              constituents: mockRedeemConstituents,
              onContinue: noop,
            }),
        },
        {
          name: 'Admin MF breakdown',
          trigger: 'OpenAdminMFBreakdown',
          onClick: () =>
            onOpen('OpenAdminMFBreakdown', {
              amount: 10000,
              hasDualBreakdown: true,
              staticConstituents: mockConstituents,
              dynamicConstituents: mockConstituents.map((fund, index) => ({
                ...fund,
                weightage: [40, 35, 25][index],
                allocationAmount: [4000, 3500, 2500][index],
              })),
            }),
        },
        {
          name: 'One-time payment breakdown',
          trigger: 'oneTimeBreakdown',
          onClick: () =>
            onOpen('oneTimeBreakdown', {
              totalAmount: 10000,
              constituents: mockConstituents,
            }),
        },
        {
          name: 'SIP payment breakdown',
          trigger: 'sipBreakdown',
          onClick: () =>
            onOpen('sipBreakdown', {
              totalAmount: 10000,
              constituents: mockConstituents,
              confirmLabel: 'Confirm',
              onConfirm: noop,
            }),
        },
        {
          name: 'Mandate details',
          trigger: 'openMandateDetails',
          onClick: () => onOpen('openMandateDetails', mockMandateDetails),
        },
        {
          name: 'Sell request form',
          trigger: 'openSellRequestForm',
          onClick: () =>
            onOpen('openSellRequestForm', {
              fund: {
                ...mockConstituents[0],
                balance: 20,
                holding: 25000,
                holdingType: 'EXTERNAL',
              },
            }),
        },
        {
          name: 'Subscription modal',
          trigger: 'openSubscription',
          onClick: () => onOpen('openSubscription', mockSubscription),
        },
      ],
    },
    {
      title: 'Payment Status',
      items: [
        {
          name: 'Payment in progress',
          trigger: 'PaymentStatusModal / inprogress',
          onClick: () => openPaymentStatus('inprogress'),
        },
        {
          name: 'Payment success',
          trigger: 'PaymentStatusModal / success',
          onClick: () => openPaymentStatus('success'),
        },
        {
          name: 'Payment failed',
          trigger: 'PaymentStatusModal / failure',
          onClick: () => openPaymentStatus('failure'),
        },
      ],
    },
  ]

  return (
    <main className='min-h-screen bg-[#F7F9FC] font-DMSans text-black-500 dark:bg-[var(--fb-bg)] dark:text-[var(--fb-text)]'>
      <div className='mx-auto max-w-[1128px] px-4 py-6 md:px-6'>
        <Button
          variant='simple'
          size='simple'
          className='mb-6 flex items-center gap-2 font-semibold text-black-500 dark:text-[var(--fb-text)]'
          onClick={() => navigate('/dashboard/mutual-funds')}
        >
          <ArrowLeft className='h-5 w-5' />
          Mutual funds
        </Button>

        <div className='mb-6 flex flex-col gap-4 border-b border-[#D3DBEC] pb-6 md:flex-row md:items-center md:justify-between'>
          <div className='flex min-w-0 items-start gap-4'>
            <div className='flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-500'>
              <Bug className='h-5 w-5' />
            </div>
            <div className='min-w-0'>
              <p className='text-xs font-semibold uppercase tracking-[0.08em] text-primary-500'>
                Debug fund
              </p>
              <h1 className='text-2xl font-semibold leading-8 text-black-500 dark:text-[var(--fb-text-strong)]'>
                Popup testing panel
              </h1>
              <p className='mt-1 max-w-2xl text-sm font-medium leading-5 text-black-400 dark:text-[var(--fb-text-muted)]'>
                Type <span className='font-semibold text-black-500'>~/debug:true</span> in fund
                search, open Debug Fund, and trigger each app popup here.
              </p>
            </div>
          </div>
        </div>

        <div className='space-y-6'>
          {popupGroups.map((group) => (
            <section key={group.title} className='rounded-lg border border-[#D3DBEC] bg-white p-4'>
              <div className='mb-4 flex items-center justify-between gap-3'>
                <h2 className='text-base font-semibold text-black-500'>{group.title}</h2>
                <span className='rounded-full bg-[#F4F7FE] px-3 py-1 text-xs font-semibold text-primary-500'>
                  {group.items.length}
                </span>
              </div>

              <div className='grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3'>
                {group.items.map((item) => (
                  <div
                    key={`${group.title}-${item.name}`}
                    className='flex min-h-[132px] flex-col justify-between rounded-lg border border-[#E6EAF2] bg-[#FBFCFE] p-4'
                  >
                    <div className='min-w-0'>
                      <h3 className='truncate text-sm font-semibold text-black-500'>{item.name}</h3>
                      <p className='mt-1 break-words text-xs font-medium text-black-300'>
                        {item.trigger}
                      </p>
                      {item.detail && (
                        <p className='mt-2 text-xs font-medium text-error-500'>{item.detail}</p>
                      )}
                    </div>

                    <Button
                      type='button'
                      variant='outline'
                      className='mt-4 h-9 w-full rounded-lg gap-2 text-xs'
                      onClick={item.onClick}
                    >
                      <PlayCircle className='h-4 w-4' />
                      {item.actionLabel || 'Trigger'}
                    </Button>
                  </div>
                ))}
              </div>
            </section>
          ))}

          <section className='rounded-lg border border-[#D3DBEC] bg-white p-4 dark:border-[var(--fb-border)] dark:bg-[var(--fb-surface)]'>
            <div className='mb-4 flex items-center justify-between gap-3'>
              <h2 className='text-base font-semibold text-black-500 dark:text-[var(--fb-text-strong)]'>
                Catastrophic Failure Components
              </h2>
              <span className='rounded-full bg-[#F4F7FE] px-3 py-1 text-xs font-semibold text-primary-500 dark:bg-[var(--fb-surface-muted)] dark:text-[var(--fb-accent-text)]'>
                2
              </span>
            </div>

            <div className='grid grid-cols-1 gap-4 xl:grid-cols-2'>
              <ApiErrorState />
              <ApiErrorState>
                <div className='fabits-api-error-actions'>
                  <Button
                    type='button'
                    className='w-full'
                    variant='fabitsWhite'
                    onClick={() => window.location.reload()}
                  >
                    Refresh
                  </Button>
                  <Button type='button' className='w-full' onClick={() => navigate('/dashboard')}>
                    Home
                  </Button>
                </div>
              </ApiErrorState>
            </div>
          </section>
        </div>
      </div>

      <PaymentStatusModal
        isOpen={showPaymentStatus}
        onClose={() => setShowPaymentStatus(false)}
        onContinue={() => setShowPaymentStatus(false)}
        onRetry={noop}
        status={paymentStatus}
        paymentData={mockPaymentData}
      />
      <KycPopup open={showKycPrompt} onClose={() => setShowKycPrompt(false)} />
    </main>
  )
}

export default DebugPopups
